<script lang="ts" setup>
  import { StoreLayout } from '@storefront/components';
  import type { OrderResource, ShippingAddress, ListOrderItemResource, Country } from '@storefront/resources';
  import type { Breadcrumb } from '@storefront/types';
  import { type FormContextInterface, useHttpClient } from '@othercommerce/foundation';
  import type { Dictionary } from '@othercommerce/domain';
  import { computed } from 'vue';

  defineOptions({
    layout: [StoreLayout],
    inheritAttrs: false,
  });

  interface ComplaintReturnMethod {
    label: string;
    description: string;
    value: number;
  }

  interface Props {
    items: ListOrderItemResource[];
    complaintReturnMethods: ComplaintReturnMethod[];
    order?: OrderResource
    returnAddress: ShippingAddress;
    countries: Country[],
    regions: Dictionary,
    complaint: string,
    breadcrumbs: Breadcrumb[]
  }

  const props = defineProps<Props>();

  const http = useHttpClient();

  const complaintItems = computed(() => {
    return props.items.map(item => ({
      id: item.id,
      orderItemComponentId: item.orderItemComponentId ?? null,
      photo: item.photo,
      variantTitle: item.variantTitle,
      stockUnit: item.stockUnit,
      amount: item.amount,
      serialNumbers: [''],
    }));
  });

  const data = computed(() => {
    return {
      complaintReturnMethod: props.complaintReturnMethods[0]?.value,
      firstName: props.order?.shippingFirstName,
      lastName: props.order?.shippingLastName,
      email: props.order?.shippingEmail,
      phone: props.order?.shippingPhone,
      countryCode: props.order?.countryCode,
      buildingNr: props.order?.shippingBuildingNr,
      apartmentNr: props.order?.shippingApartmentNr,
      street: props.order?.shippingStreet,
      postcode: props.order?.shippingPostcode,
      city: props.order?.shippingCity,
      region: props.order?.shippingRegion,
      items: complaintItems.value,
    };
  });

  function removeSerialNumber(ctx: FormContextInterface, itemIndex: number, serialIndex: number) {
    ctx.data.value.items[itemIndex].serialNumbers.splice(serialIndex, 1);
  }

  function addSerialNumber(ctx: FormContextInterface, itemIndex: number) {
    ctx.data.value.items[itemIndex].serialNumbers.push('');
  }

  function onSubmit(data: any, complaint: string) {
    const formData = new FormData();

    formData.append('complaintReturnMethod', data.complaintReturnMethod ?? '');
    formData.append('firstName', data.firstName ?? '');
    formData.append('lastName', data.lastName ?? '');
    formData.append('email', data.email ?? '');
    formData.append('phone', data.phone ?? '');
    formData.append('countryCode', data.countryCode ?? '');
    formData.append('buildingNr', data.buildingNr ?? '');
    formData.append('apartmentNr', data.apartmentNr ?? '');
    formData.append('street', data.street ?? '');
    formData.append('postcode', data.postcode ?? '');
    formData.append('city', data.city ?? '');
    formData.append('region', data.region ?? '');
    formData.append('reason', data.reason ?? '');
    formData.append('description', data.description ?? '');

    if (Array.isArray(data.items)) {
      data.items.forEach((item: any, i: number) => {
        formData.append(`items[${i}][id]`, item.id);
        formData.append(`items[${i}][orderItemComponentId]`, item.orderItemComponentId ?? '');

        if (Array.isArray(item.serialNumbers)) {
          item.serialNumbers.forEach((number: string, numberIndex: number) => {
            formData.append(`items[${i}][serialNumbers][${numberIndex}]`, number);
          });
        }
      });
    }

    const files = data.files instanceof FileList
      ? Array.from(data.files)
      : Array.isArray(data.files)
        ? data.files
        : [];

    for (const file of files) {
      formData.append('files[]', file);
    }

    return http.post(complaint, formData);
  }
</script>
<template>
  <main class="complaints">
    <SfBreadcrumbs :breadcrumbs />
    <FormController v-slot="{ processing, data, ctx }" :data="data" @submit="(data) => onSubmit(data, complaint)">
      <SfTile>
        <template #title>
          <h2>{{ $t('SHOP.ACCOUNT.HEADER.COMPLAINT_PRODUCTS') }}</h2>
        </template>
        <ul class="complaints__items">
          <li v-for="(item, i) in data.items" class="complaints__item item">
            <div class="item__header">
              <div class="item__title">
                <MediaPicture :photo="item.photo" sizes="160x160 2x, 80x80" />
                <span>
                  {{ item.variantTitle }} -
                  <SfUnit :quantity="item.amount" :unit="item.stockUnit" />
                </span>
              </div>
            </div>
            <div class="item__serial-numbers">
              <div v-for="(_, j) in item.serialNumbers" class="item__serial-number">
                <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.COMPLAINT_PRODUCT_SN', { number: j + 1 })" :name="`items.${i}.serialNumbers[${j}]`" />
                <SfButton :disabled="data.items[i].serialNumbers.length === 1" :title="$t('SHOP.ACTION.DESTROY')" danger square transparent @click.stop="removeSerialNumber(ctx, i, j)">
                  <template #before>
                    <SfIconTrash aria-hidden="true" />
                  </template>
                </SfButton>
              </div>
              <SfButton :disabled="data.items[i].serialNumbers.length >= data.items[i].amount" primary small @click="addSerialNumber(ctx, i)">
                <template #default>
                  {{ $t('SHOP.ACCOUNT.ACTION.COMPLAINT_ADD_ITEM') }}
                </template>
              </SfButton>
            </div>
          </li>
        </ul>
      </SfTile>

      <SfTile>
        <template #title>
          <h2>{{ $t('SHOP.ACCOUNT.HEADER.COMPLAINT_DETAILS') }}</h2>
        </template>

        <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.COMPLAINT_REASON_EXTENDED')" name="reason" required />
        <SfFormTextarea :label="$t('SHOP.ACCOUNT.LABEL.COMPLAINT_DESCRIPTION_EXTENDED')" name="description" required />
        <SfFormSelect :label="$t('SHOP.ACCOUNT.LABEL.COMPLAINT_CHOOSE_RETURN_METHOD')" :options="complaintReturnMethods" name="complaintReturnMethod" required />

        <ul class="complaints__return-methods">
          <li v-for="method in complaintReturnMethods">
            <strong>{{ method.label }}</strong> - {{ method.description }}
          </li>
        </ul>

        <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.COMPLAINT_UPLOAD')" multiple name="files" type="file" />
      </SfTile>

      <SfTile>
        <template #title>
          <h2>{{ $t('SHOP.ACCOUNT.HEADER.COMPLAINT_CONTACT') }}</h2>
        </template>

        <SfFormInput :label="$t('SHOP.COMMON.LABEL.FIRSTNAME')" name="firstName" required type="text" />
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.LASTNAME')" name="lastName" required type="text" />

        <SfFormInput :label="$t('SHOP.COMMON.LABEL.EMAIL')" name="email" required type="email" />
        <SfFormInput :label="$t('SHOP.COMMON.LABEL.PHONE')" name="phone" required type="tel" />

        <SfAddressForm :countries :regions />
      </SfTile>

      <SfTile>
        <template #title>
          <h2>{{ $t('SHOP.ACCOUNT.HEADER.COMPLAINT_RETURN_ADDRESS') }}</h2>
        </template>
        <address>
          <p v-if="returnAddress?.countryCode" v-html="returnAddress?.countryCode" />
          <p v-if="returnAddress?.name" v-html="returnAddress?.name" />
          <p v-if="returnAddress?.company" v-html="returnAddress?.company" />
          <p v-if="returnAddress?.firstLine" v-html="returnAddress?.firstLine" />
          <p v-if="returnAddress?.secondLine" v-html="returnAddress?.secondLine" />
          <p v-if="returnAddress?.thirdLine" v-html="returnAddress?.thirdLine" />
          <p v-if="returnAddress?.region" v-html="returnAddress?.region" />
          <p v-if="returnAddress?.email" v-html="returnAddress?.email" />
          <p v-if="returnAddress?.phone" v-html="returnAddress?.phone" />
        </address>
      </SfTile>

      <SfTile>
        <template #title>
          <h2>{{ $t('SHOP.ACCOUNT.HEADER.COMPLAINT_PROCESSING_WARNING') }}</h2>
        </template>
        <template #default>
          <p>{{ $t('SHOP.ACCOUNT.MESSAGE.COMPLAINT_PROCESSING_NOTICE') }}</p>
        </template>
      </SfTile>

      <div class="complaints__button-wrapper">
        <SfButton :loading="processing" primary type="submit">
          {{ $t('SHOP.ACCOUNT.ACTION.COMPLAINT_CREATE') }}
        </SfButton>
      </div>
    </FormController>
  </main>

  <RouterView />
</template>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .item {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem 0;

    &__header {
      display: grid;
      grid-template-columns: 1fr;
      align-items: flex-start;
      gap: 1rem;
    }

    &__title {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &__serial-numbers {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      width: 30%;

      @include media.query(lg) {
        width: 35%;
      }

      @include media.query(md) {
        width: 60%;
      }

      @include media.query(sm) {
        width: 100%;
      }
    }

    &__serial-number {
      display: flex;
      gap: 1rem;
    }
  }

  .complaints {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin: 0 auto 100px;
    width: 98%;
    max-width: var(--sf-account-max-width);

    &__items {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;

      @include media.query(sm) {
        gap: 1rem;
      }
    }

    &__return-methods {
      list-style: none;
    }

    &__button-wrapper {
      display: flex;
      justify-content: flex-end;
    }
  }
</style>
